import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Crown, Heart, Smiley, SmileyMeh, SmileySad, ChatsCircle,
  PaperPlaneTilt, Plus, Trash, CaretDown, CaretRight, Megaphone, Sparkle,
} from "@phosphor-icons/react";

export const SENTIMENTS = [
  { key: "love", label: "Love it", Icon: Heart },
  { key: "good", label: "Good", Icon: Smiley },
  { key: "confused", label: "Confused", Icon: SmileyMeh },
  { key: "frustrated", label: "Frustrated", Icon: SmileySad },
];

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch (e) { return ""; }
};

function AuthorLine({ name, isFounding, isTeam, date }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-[#8A867D]">
      {isFounding && <Crown size={13} weight="fill" className="text-[#B47A3C]" />}
      {isTeam && <Sparkle size={13} weight="fill" className="text-[#B47A3C]" />}
      <span className={isTeam ? "font-semibold text-[#B47A3C]" : ""}>{name}</span>
      {date && <span>· {fmtDate(date)}</span>}
    </span>
  );
}

function PulseCard() {
  const [sentiment, setSentiment] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!sentiment) { toast.error("Pick how it's going first."); return; }
    setBusy(true);
    try {
      await api.post("/feedback", { sentiment, comment: comment.trim(), page: "/community" });
      setSent(true);
      toast.success("Thanks — the team reads every single one. 🙏");
    } catch (e) {
      toast.error("Couldn't send that — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="border-2 border-[#0D1B2A] bg-[#0D1B2A] text-[#FDFBF7] p-7" data-testid="pulse-thanks">
        <p className="font-serif-display text-2xl font-bold mb-1">Received — thank you.</p>
        <p className="text-[15px] text-[#D8D3C8]">Your words steer what we build next. That's the whole point of this room.</p>
        <button onClick={() => { setSent(false); setSentiment(""); setComment(""); }} data-testid="pulse-again" className="mt-4 text-sm font-semibold text-[#B47A3C] hover:text-[#FDFBF7] transition-colors">Send another note →</button>
      </div>
    );
  }

  return (
    <div className="border-2 border-[#0D1B2A] bg-white p-7" data-testid="pulse-card">
      <p className="label-caps text-[#B47A3C] mb-1">How are we doing?</p>
      <h2 className="font-serif-display text-2xl sm:text-3xl font-bold tracking-tight mb-4">Tell us straight — it takes 10 seconds.</h2>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="pulse-sentiments">
        {SENTIMENTS.map((s) => (
          <button
            key={s.key}
            onClick={() => setSentiment(s.key)}
            data-testid={`pulse-sentiment-${s.key}`}
            className={`h-11 px-4 inline-flex items-center gap-2 border font-semibold text-[15px] transition-colors ${sentiment === s.key ? "bg-[#0D1B2A] text-[#FDFBF7] border-[#0D1B2A]" : "bg-white text-[#0D1B2A] border-[#0D1B2A]/25 hover:border-[#0D1B2A]"}`}
          >
            <s.Icon size={18} weight={sentiment === s.key ? "fill" : "regular"} className={sentiment === s.key ? "text-[#B47A3C]" : ""} /> {s.label}
          </button>
        ))}
      </div>
      <textarea
        data-testid="pulse-comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={2}
        maxLength={2000}
        placeholder="Anything specific? (optional — but the details are gold)"
        className="w-full border border-[#0D1B2A]/25 rounded-none px-3 py-2.5 text-[15px] focus:border-[#0D1B2A] outline-none resize-none mb-3"
      />
      <button onClick={submit} disabled={busy} data-testid="pulse-submit" className="h-12 px-6 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium inline-flex items-center gap-2 hover:bg-black transition-transform active:scale-[0.98] disabled:opacity-60">
        {busy ? "Sending…" : "Send feedback"} <PaperPlaneTilt size={16} weight="fill" />
      </button>
    </div>
  );
}

function YouSaidWeDid() {
  const [items, setItems] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [youSaid, setYouSaid] = useState("");
  const [weDid, setWeDid] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/community/updates").then(({ data }) => { setItems(data.items || []); setIsAdmin(!!data.is_admin); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const post = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/community/updates", { you_said: youSaid.trim(), we_did: weDid.trim() });
      setYouSaid(""); setWeDid(""); setShowForm(false);
      toast.success("Update posted — the loop is closed. 🔄");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't post that");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this update?")) return;
    try { await api.delete(`/community/updates/${id}`); load(); } catch (e) { toast.error("Couldn't remove that"); }
  };

  if (!items.length && !isAdmin) return null;

  return (
    <section className="mt-12" data-testid="you-said-we-did">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Megaphone size={20} weight="fill" className="text-[#B47A3C]" />
          <h2 className="font-serif-display text-2xl sm:text-3xl font-bold tracking-tight">You said → We did</h2>
        </div>
        {isAdmin && (
          <button onClick={() => setShowForm(!showForm)} data-testid="ysd-add-toggle" className="h-10 px-4 border border-[#0D1B2A] rounded-none text-sm font-semibold hover:bg-[#0D1B2A] hover:text-white transition-colors inline-flex items-center gap-1.5">
            <Plus size={15} weight="bold" /> Post an update
          </button>
        )}
      </div>
      <p className="text-[15px] text-[#525252] mb-5 max-w-2xl">Your feedback doesn't disappear into a void. When your words become features, this is where we show the receipts.</p>
      {showForm && (
        <form onSubmit={post} className="border-2 border-[#0D1B2A] bg-white p-5 mb-5" data-testid="ysd-form">
          <label className="label-caps block mb-1.5">You said…</label>
          <input data-testid="ysd-you-said" value={youSaid} onChange={(e) => setYouSaid(e.target.value)} maxLength={500} placeholder="The feedback, in the seller's words" className="h-11 w-full border border-[#0D1B2A]/30 rounded-none px-3 text-[15px] focus:border-[#0D1B2A] outline-none mb-3" />
          <label className="label-caps block mb-1.5">We did…</label>
          <input data-testid="ysd-we-did" value={weDid} onChange={(e) => setWeDid(e.target.value)} maxLength={500} placeholder="What shipped because of it" className="h-11 w-full border border-[#0D1B2A]/30 rounded-none px-3 text-[15px] focus:border-[#0D1B2A] outline-none mb-4" />
          <button type="submit" disabled={busy} data-testid="ysd-submit" className="h-11 px-5 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium disabled:opacity-60">{busy ? "Posting…" : "Post update"}</button>
        </form>
      )}
      <div className="space-y-3">
        {items.map((u, i) => (
          <div key={u.id} data-testid={`ysd-item-${i}`} className="border border-[#0D1B2A]/20 bg-white p-5 grid md:grid-cols-[1fr_auto_1fr] gap-3 md:gap-5 items-center">
            <p className="text-[15px] italic text-[#525252] leading-snug">&ldquo;{u.you_said}&rdquo;</p>
            <CaretRight size={20} weight="bold" className="text-[#B47A3C] hidden md:block" />
            <div className="flex items-start justify-between gap-3">
              <p className="text-[15px] font-semibold text-[#0D1B2A] leading-snug">{u.we_did}</p>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-[#8A867D] whitespace-nowrap">{fmtDate(u.created_at)}</span>
                {isAdmin && (
                  <button onClick={() => remove(u.id)} data-testid={`ysd-delete-${i}`} className="text-[#8A867D] hover:text-[#B47A3C]"><Trash size={14} /></button>
                )}
              </div>
            </div>
          </div>
        ))}
        {!items.length && <p className="text-[15px] text-[#8A867D] border border-dashed border-[#D8D3C8] p-5" data-testid="ysd-empty">No updates yet — the first one lands as soon as member feedback ships a change.</p>}
      </div>
    </section>
  );
}

function ThreadCard({ t, isAdmin, onChanged }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState(t);

  useEffect(() => setThread(t), [t]);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) {
      try {
        const { data } = await api.get(`/community/threads/${thread.id}`);
        setDetail(data);
      } catch (e) { toast.error("Couldn't load replies"); }
    }
  };

  const like = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/community/threads/${thread.id}/like`);
      setThread((prev) => ({ ...prev, like_count: data.like_count, has_liked: data.has_liked }));
    } catch (err) { toast.error("Couldn't record that"); }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/community/threads/${thread.id}/replies`, { body: reply.trim() });
      setDetail(data);
      setThread((prev) => ({ ...prev, reply_count: data.reply_count }));
      setReply("");
    } catch (err) {
      toast.error("Couldn't post your reply");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Remove this discussion?")) return;
    try {
      await api.delete(`/community/threads/${thread.id}`);
      toast.success("Discussion removed.");
      onChanged();
    } catch (err) { toast.error("Couldn't remove that"); }
  };

  const replies = detail?.replies || [];

  return (
    <div className="border border-[#0D1B2A]/20 bg-white" data-testid={`thread-card-${thread.id}`}>
      <div role="button" tabIndex={0} onClick={toggleOpen} onKeyDown={(e) => { if (e.key === "Enter") toggleOpen(); }} data-testid={`thread-toggle-${thread.id}`} className="w-full text-left p-5 flex items-start gap-4 hover:bg-[#FDFBF7] transition-colors cursor-pointer">
        <div className="shrink-0 mt-1">
          {open ? <CaretDown size={16} weight="bold" className="text-[#B47A3C]" /> : <CaretRight size={16} weight="bold" className="text-[#B47A3C]" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-serif-display text-xl font-bold leading-tight">{thread.title}</h3>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full border border-[#D8D3C8] text-[#525252] bg-[#F5F3ED]">{thread.category}</span>
          </div>
          <AuthorLine name={thread.author_name} isFounding={thread.author_is_founding} isTeam={thread.is_team} date={thread.created_at} />
        </div>
        <div className="shrink-0 flex items-center gap-3">
          <span className="inline-flex items-center gap-1 text-sm text-[#8A867D]"><ChatsCircle size={16} weight="fill" /> {thread.reply_count}</span>
          <button onClick={like} data-testid={`thread-like-${thread.id}`} className={`inline-flex items-center gap-1 text-sm font-semibold transition-colors ${thread.has_liked ? "text-[#B47A3C]" : "text-[#8A867D] hover:text-[#B47A3C]"}`}>
            <Heart size={16} weight={thread.has_liked ? "fill" : "regular"} /> {thread.like_count}
          </button>
          {(isAdmin || thread.is_mine) && (
            <button onClick={remove} data-testid={`thread-delete-${thread.id}`} className="text-[#8A867D] hover:text-[#B47A3C]"><Trash size={15} /></button>
          )}
        </div>
      </div>
      {open && (
        <div className="px-5 pb-5 pl-14" data-testid={`thread-detail-${thread.id}`}>
          {thread.body && <p className="text-[15px] text-[#3A3730] leading-relaxed mb-4 whitespace-pre-line">{thread.body}</p>}
          <div className="space-y-3 mb-4">
            {replies.map((r, i) => (
              <div key={r.id} data-testid={`thread-reply-${i}`} className="border-l-2 border-[#E5E0D6] pl-4">
                <AuthorLine name={r.author_name} isFounding={r.author_is_founding} date={r.created_at} />
                <p className="text-[15px] text-[#3A3730] leading-relaxed mt-1 whitespace-pre-line">{r.body}</p>
              </div>
            ))}
            {detail && !replies.length && <p className="text-sm text-[#8A867D]">No replies yet — be the first.</p>}
            {!detail && <p className="text-sm text-[#8A867D]">Loading replies…</p>}
          </div>
          <form onSubmit={sendReply} className="flex gap-2">
            <input
              data-testid={`thread-reply-input-${thread.id}`}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              maxLength={4000}
              placeholder="Add your two cents…"
              className="h-11 flex-1 border border-[#0D1B2A]/25 rounded-none px-3 text-[15px] focus:border-[#0D1B2A] outline-none"
            />
            <button type="submit" disabled={busy || !reply.trim()} data-testid={`thread-reply-submit-${thread.id}`} className="h-11 px-4 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
              <PaperPlaneTilt size={15} weight="fill" /> Reply
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default function CommunityPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("General");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/community/threads");
      setThreads(data.items || []);
      setCategories(data.categories || []);
      setIsAdmin(!!data.is_admin);
    } catch (e) {
      toast.error("Couldn't load the community");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Give your discussion a title."); return; }
    setBusy(true);
    try {
      await api.post("/community/threads", { title: title.trim(), body: body.trim(), category });
      setTitle(""); setBody(""); setShowForm(false);
      toast.success("Posted — the community can see it now. 🙌");
      load();
    } catch (err) {
      toast.error("Couldn't post — please try again");
    } finally {
      setBusy(false);
    }
  };

  const counts = useMemo(() => {
    const c = { all: threads.length };
    for (const t of threads) c[t.category] = (c[t.category] || 0) + 1;
    return c;
  }, [threads]);

  const filtered = filter === "all" ? threads : threads.filter((t) => t.category === filter);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="community-page">
      <header className="sticky top-0 z-30 border-b border-[#D8D3C8] bg-[#FDFBF7]/90 backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/vv_logo.jpg" alt="Vintage Vantage" onClick={() => navigate("/dashboard")} className="h-9 w-9 rounded-full object-cover ring-1 ring-[#E5E0D6] cursor-pointer shrink-0 hover:ring-[#B47A3C] transition-all" data-testid="app-compass-mark" title="Vintage Vantage — home" />
            <button onClick={() => navigate("/dashboard")} data-testid="community-back-button" className="inline-flex items-center gap-2 font-medium hover:text-[#B47A3C] transition-colors">
              <ArrowLeft size={18} weight="bold" /> Dashboard
            </button>
          </div>
          <span className="text-sm font-semibold text-[#525252] truncate max-w-[200px]">{user?.name || user?.email}</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 mb-2"><ChatsCircle size={22} weight="fill" className="text-[#B47A3C]" /> <span className="label-caps">The Seller Community</span></div>
        <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-none font-medium mb-3">Ask. Share. Steer.</h1>
        <p className="text-lg text-[#525252] leading-relaxed max-w-2xl mb-8">
          Ask experts, compare notes on rare finds, and tell us — bluntly — how we're doing. Every word here
          feeds the Knowledge Flywheel that makes this tool better for every seller.
        </p>

        <PulseCard />
        <YouSaidWeDid />

        {/* Discussions */}
        <section className="mt-12" data-testid="community-discussions">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="font-serif-display text-2xl sm:text-3xl font-bold tracking-tight">Discussions</h2>
            <button onClick={() => setShowForm(!showForm)} data-testid="thread-new-toggle" className="h-11 px-5 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium inline-flex items-center gap-2 hover:bg-black transition-transform active:scale-[0.98]">
              <Plus size={16} weight="bold" /> Start a discussion
            </button>
          </div>

          {showForm && (
            <form onSubmit={submit} className="border-2 border-[#0D1B2A] bg-white p-6 mb-6" data-testid="thread-form">
              <div className="grid sm:grid-cols-[1fr_auto] gap-3 mb-3">
                <input data-testid="thread-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} placeholder="What's on your mind?" className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-3 text-[16px] focus:border-[#0D1B2A] outline-none" />
                <select data-testid="thread-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className="h-12 border border-[#0D1B2A]/30 rounded-none px-2 text-[15px] focus:border-[#0D1B2A] outline-none bg-white">
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <textarea data-testid="thread-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={4000} placeholder="Details, photos links, questions — whatever helps the community help you." className="w-full border border-[#0D1B2A]/30 rounded-none px-3 py-2.5 text-[15px] focus:border-[#0D1B2A] outline-none resize-none mb-4" />
              <div className="flex items-center gap-2">
                <button type="submit" disabled={busy} data-testid="thread-submit" className="h-12 px-5 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium disabled:opacity-60">{busy ? "Posting…" : "Post discussion"}</button>
                <button type="button" onClick={() => { setShowForm(false); setTitle(""); setBody(""); }} className="h-12 px-4 border border-[#0D1B2A] rounded-none font-medium hover:bg-[#F5F3ED] transition-colors">Cancel</button>
              </div>
            </form>
          )}

          <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="community-filters">
            {[["all", "All"], ...categories.map((c) => [c, c])].map(([key, label]) => (
              <button key={key} onClick={() => setFilter(key)} data-testid={`community-filter-${key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`text-sm font-semibold px-3 py-1.5 rounded-full border transition-colors ${filter === key ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "bg-white text-[#525252] border-[#D8D3C8] hover:border-[#0D1B2A]"}`}>
                {label} <span className={filter === key ? "text-white/70" : "text-[#8A867D]"}>{counts[key] || 0}</span>
              </button>
            ))}
          </div>

          {loading ? (
            <div className="p-10 text-center text-[#8A867D]">Loading discussions…</div>
          ) : filtered.length === 0 ? (
            <div className="border border-[#0D1B2A]/20 bg-white p-10 text-center text-[#525252]" data-testid="community-empty">Nothing here yet — start the first discussion.</div>
          ) : (
            <div className="space-y-3" data-testid="community-thread-list">
              {filtered.map((t) => <ThreadCard key={t.id} t={t} isAdmin={isAdmin} onChanged={load} />)}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
